/**
 * ReorderableList.tsx — the ONE shared reorder component (design open
 * question 1, resolved: one component, not per-context variants) used by the
 * agentic editor's Facts tab (E12-S3a) and, in S3b, the Scripts tab's step
 * list and per-step `required_facts` order.
 *
 * Native HTML5 drag (no library, D1: at most 60 facts / 40 steps — well
 * within what native drag handles cleanly) PLUS a keyboard-reachable
 * "Mover para cima" / "Mover para baixo" pair per row (accessibility notes,
 * design doc) — reorder never depends on pointer drag.
 *
 * Focus after reorder stays on the moved row's mover button, not reset to
 * the list top — the operator is mid-task, likely moving the same item more
 * than once (design doc, accessibility notes).
 */

import { useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export interface ReorderableListProps<T> {
  items: T[];
  getId: (item: T) => string;
  onReorder: (nextItems: T[]) => void;
  renderItem: (item: T) => ReactNode;
  ariaLabel: string;
  disabled?: boolean;
}

export const ReorderableList = <T,>({
  items,
  getId,
  onReorder,
  renderItem,
  ariaLabel,
  disabled = undefined,
}: ReorderableListProps<T>) => {
  const { t } = useTranslation();
  const dragIndexRef = useRef<number | null>(null);
  const moveButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const move = (from: number, to: number) => {
    if (to < 0 || to >= items.length || from === to) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved!);
    onReorder(next);
    const id = getId(moved!);
    // Focus follows the moved row after the reordered list re-renders.
    requestAnimationFrame(() => moveButtonRefs.current[`${id}-up`]?.focus());
  };

  return (
    <ul aria-label={ariaLabel} className="flex flex-col gap-1">
      {items.map((item, index) => {
        const id = getId(item);
        return (
          <li
            key={id}
            draggable={!disabled}
            data-testid={`reorder-row-${id}`}
            onDragStart={() => {
              dragIndexRef.current = index;
            }}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              const from = dragIndexRef.current;
              dragIndexRef.current = null;
              if (from != null) move(from, index);
            }}
            className="flex items-center gap-2 rounded-lg bg-surface-1 px-2 py-1.5 ring-1 ring-inset ring-ink-3/10"
          >
            <span aria-hidden="true" className="cursor-grab select-none text-ink-3">
              ⠿
            </span>
            <div className="flex flex-1 items-center gap-2">{renderItem(item)}</div>
            <div className="flex flex-shrink-0 items-center gap-1">
              <button
                type="button"
                ref={(node) => {
                  moveButtonRefs.current[`${id}-up`] = node;
                }}
                data-testid={`reorder-${id}-up`}
                aria-label={t('config.edit.reorder.up')}
                disabled={disabled || index === 0}
                onClick={() => move(index, index - 1)}
                className="inline-flex h-7 w-7 items-center justify-center rounded text-meta text-ink-2 hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-outline-focus disabled:opacity-40"
              >
                ↑
              </button>
              <button
                type="button"
                ref={(node) => {
                  moveButtonRefs.current[`${id}-down`] = node;
                }}
                data-testid={`reorder-${id}-down`}
                aria-label={t('config.edit.reorder.down')}
                disabled={disabled || index === items.length - 1}
                onClick={() => move(index, index + 1)}
                className="inline-flex h-7 w-7 items-center justify-center rounded text-meta text-ink-2 hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-outline-focus disabled:opacity-40"
              >
                ↓
              </button>
            </div>
          </li>
        );
      })}
    </ul>
  );
};
